import React, { useState, useEffect } from 'react';
import { AdminApiService, GameRecord, GameFilters } from '../../services/adminApiService';
import GameReplay from '../GameReplay';
import './GameHistoryViewer.css';

// Helper function to format game timestamps in user's local timezone
// SQLite stores timestamps in UTC, so we need to parse them as UTC and convert to local
const formatGameTimestamp = (timestamp: string, format: 'date' | 'time' | 'datetime' = 'datetime'): string => {
  if (!timestamp) return '';
  
  // If timestamp doesn't have timezone info, assume it's UTC (SQLite format)
  // SQLite format: "YYYY-MM-DD HH:MM:SS"
  let date: Date;
  if (timestamp.includes('T') || timestamp.includes('Z') || timestamp.includes('+')) {
    // Already has timezone info (ISO format)
    date = new Date(timestamp);
  } else {
    // SQLite format without timezone - treat as UTC
    date = new Date(timestamp + ' UTC');
  }
  
  // Format based on requested format
  if (format === 'date') {
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } else if (format === 'time') {
    return date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } else {
    return date.toLocaleString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }
};

interface GroupedGame {
  game_id: string;
  whitePlayer: GameRecord | null;
  blackPlayer: GameRecord | null;
  timestamp: string;
  game_mode: string;
  game_end_reason: string | null;
  winner: 'white' | 'black' | 'draw';
}

export const GameHistoryViewer: React.FC = () => {
  const [games, setGames] = useState<GameRecord[]>([]);
  const [filteredGames, setFilteredGames] = useState<GroupedGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReplay, setSelectedReplay] = useState<{
    gameId: string;
    opponent: string;
    result: 'win' | 'loss' | 'draw';
    playerColor: 'white' | 'black';
    timestamp: string;
    winType: 'igo' | 'wego' | null;
    gameEndReason: string | null;
  } | null>(null);
  
  // Filter state
  const [filters, setFilters] = useState<GameFilters>({
    dateFrom: '',
    dateTo: '',
    gameMode: '',
    result: undefined,
    playerSearch: '',
    limit: 500,
  });

  useEffect(() => {
    loadGames();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [games, filters]);

  const loadGames = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await AdminApiService.getAllGames({ limit: 1000 });
      if (result.error) {
        setError(result.error);
      } else {
        setGames(result.games);
      }
    } catch (err) {
      setError('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  // Group games by game_id and create single entries
  const groupGamesByGameId = (gameList: GameRecord[]): GroupedGame[] => {
    const gameMap = new Map<string, GameRecord[]>();
    const ungroupedGames: GameRecord[] = [];
    
    // Group games by game_id
    gameList.forEach(game => {
      if (game.game_id) {
        if (!gameMap.has(game.game_id)) {
          gameMap.set(game.game_id, []);
        }
        gameMap.get(game.game_id)!.push(game);
      } else {
        // Games without game_id (old games) - keep as ungrouped
        ungroupedGames.push(game);
      }
    });
    
    // Create grouped entries (one per game)
    const grouped: GroupedGame[] = [];
    
    gameMap.forEach((gameRecords, gameId) => {
      const whiteRecord = gameRecords.find(g => g.player_color === 'white');
      const blackRecord = gameRecords.find(g => g.player_color === 'black');
      
      // Determine winner from records
      let winner: 'white' | 'black' | 'draw' = 'draw';
      if (whiteRecord?.result === 'win') winner = 'white';
      else if (blackRecord?.result === 'win') winner = 'black';
      else if (whiteRecord?.result === 'draw' || blackRecord?.result === 'draw') winner = 'draw';
      
      grouped.push({
        game_id: gameId,
        whitePlayer: whiteRecord || null,
        blackPlayer: blackRecord || null,
        timestamp: whiteRecord?.timestamp || blackRecord?.timestamp || '',
        game_mode: whiteRecord?.game_mode || blackRecord?.game_mode || '',
        game_end_reason: whiteRecord?.game_end_reason || blackRecord?.game_end_reason || null,
        winner
      });
    });
    
    // Add ungrouped games (old games without game_id) - create entries for them too
    ungroupedGames.forEach(game => {
      // For ungrouped games, we'll show them individually but try to match by opponent and timestamp
      // For now, just create a single entry
      const winner = game.result === 'win' 
        ? (game.player_color === 'white' ? 'white' : 'black')
        : game.result === 'draw' ? 'draw' : (game.player_color === 'white' ? 'black' : 'white');
      
      grouped.push({
        game_id: game.id, // Use record ID as fallback
        whitePlayer: game.player_color === 'white' ? game : null,
        blackPlayer: game.player_color === 'black' ? game : null,
        timestamp: game.timestamp,
        game_mode: game.game_mode,
        game_end_reason: game.game_end_reason || null,
        winner: winner as 'white' | 'black' | 'draw'
      });
    });
    
    return grouped;
  };

  // Format game end reason for display
  const formatGameEndReason = (reason: string | null | undefined, winner: 'white' | 'black' | 'draw'): string => {
    if (!reason) {
      if (winner === 'draw') return 'Draw';
      return winner === 'white' ? 'White wins' : 'Black wins';
    }
    
    const reasonMap: { [key: string]: string } = {
      'igo': 'Igo',
      'wego': 'Wego',
      'resignation': 'Resignation',
      'timeout': 'Timeout',
      'disconnect': 'Disconnect',
      'draw': 'Draw',
      'server_shutdown': 'Server Shutdown'
    };
    
    const reasonText = reasonMap[reason] || reason;
    
    if (winner === 'draw') {
      return 'Draw';
    }
    
    const winnerText = winner === 'white' ? 'White wins' : 'Black wins';
    return `${winnerText} (${reasonText})`;
  };

  const applyFilters = () => {
    let filtered = [...games];

    // Apply date range filter
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(game => {
        const gameDate = new Date(game.timestamp);
        return gameDate >= fromDate;
      });
    }
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(game => {
        const gameDate = new Date(game.timestamp);
        return gameDate <= toDate;
      });
    }

    // Apply game mode filter
    if (filters.gameMode) {
      filtered = filtered.filter(game => 
        game.game_mode.toLowerCase().includes(filters.gameMode!.toLowerCase())
      );
    }

    // Apply result filter (check if winner matches)
    if (filters.result) {
      // For grouped games, we'll filter after grouping
      // For now, keep all games that could match
      filtered = filtered.filter(game => {
        if (filters.result === 'win') {
          return game.result === 'win';
        } else if (filters.result === 'loss') {
          return game.result === 'loss';
        } else if (filters.result === 'draw') {
          return game.result === 'draw';
        }
        return true;
      });
    }

    // Apply player search filter
    if (filters.playerSearch) {
      const search = filters.playerSearch.toLowerCase();
      filtered = filtered.filter(game => 
        (game.user_username || '').toLowerCase().includes(search) ||
        (game.opponent_username || game.opponent || '').toLowerCase().includes(search)
      );
    }

    // Group games by game_id
    const grouped = groupGamesByGameId(filtered);
    
    // Apply result filter on grouped games
    let finalGrouped = grouped;
    if (filters.result) {
      finalGrouped = grouped.filter(group => {
        if (filters.result === 'win') {
          return group.winner !== 'draw';
        } else if (filters.result === 'draw') {
          return group.winner === 'draw';
        }
        return true;
      });
    }
    
    // Sort by timestamp (newest first)
    finalGrouped.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Apply limit
    if (filters.limit && filters.limit > 0) {
      finalGrouped = finalGrouped.slice(0, filters.limit);
    }

    setFilteredGames(finalGrouped);
  };

  const handleFilterChange = (key: keyof GameFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      gameMode: '',
      result: undefined,
      playerSearch: '',
      limit: 500,
    });
  };

  // Helper function to get date string in YYYY-MM-DD format
  const getDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Quick filter handlers
  const handleQuickFilter = (filter: 'today' | 'thisWeek' | 'thisMonth') => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filter === 'today') {
      setFilters(prev => ({ ...prev, dateFrom: getDateString(today), dateTo: getDateString(today) }));
    } else if (filter === 'thisWeek') {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
      setFilters(prev => ({ ...prev, dateFrom: getDateString(weekStart), dateTo: getDateString(today) }));
    } else if (filter === 'thisMonth') {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      setFilters(prev => ({ ...prev, dateFrom: getDateString(monthStart), dateTo: getDateString(today) }));
    }
  };

  if (loading) {
    return (
      <div className="game-history-viewer">
        <div className="game-history-loading">Loading game history...</div>
      </div>
    );
  }

  return (
    <div className="game-history-viewer">
      <div className="game-history-header">
        <h2>Game History Viewer</h2>
        <div className="game-history-actions">
          <button onClick={loadGames} className="game-history-refresh-btn">Refresh</button>
          <button onClick={clearFilters} className="game-history-clear-btn">Clear Filters</button>
        </div>
      </div>

      {error && <div className="game-history-error">{error}</div>}

      <div className="game-history-filters">
        <div className="game-history-filter-row">
          <div className="game-history-filter-group">
            <label>Date From:</label>
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="game-history-filter-input"
            />
          </div>
          
          <div className="game-history-filter-group">
            <label>Date To:</label>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="game-history-filter-input"
            />
          </div>
          
          <div className="game-history-filter-group">
            <label>Quick Filters:</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                onClick={() => handleQuickFilter('today')}
                className="game-history-quick-filter-btn"
              >
                Today
              </button>
              <button
                onClick={() => handleQuickFilter('thisWeek')}
                className="game-history-quick-filter-btn"
              >
                Week
              </button>
              <button
                onClick={() => handleQuickFilter('thisMonth')}
                className="game-history-quick-filter-btn"
              >
                Month
              </button>
            </div>
          </div>
          
          <div className="game-history-filter-group">
            <label>Match Type:</label>
            <input
              type="text"
              value={filters.gameMode || ''}
              onChange={(e) => handleFilterChange('gameMode', e.target.value)}
              placeholder="Filter by match type..."
              className="game-history-filter-input"
            />
          </div>
          
          <div className="game-history-filter-group">
            <label>Result:</label>
            <select
              value={filters.result || ''}
              onChange={(e) => handleFilterChange('result', e.target.value || undefined)}
              className="game-history-filter-select"
            >
              <option value="">All Results</option>
              <option value="win">Win</option>
              <option value="loss">Loss</option>
              <option value="draw">Draw</option>
            </select>
          </div>
        </div>

        <div className="game-history-filter-row">
          <div className="game-history-filter-group">
            <label>Player Search:</label>
            <input
              type="text"
              value={filters.playerSearch || ''}
              onChange={(e) => handleFilterChange('playerSearch', e.target.value)}
              placeholder="Search by player name..."
              className="game-history-filter-input"
            />
          </div>
          
          <div className="game-history-filter-group">
            <label>Limit:</label>
            <input
              type="number"
              min="1"
              max="5000"
              value={filters.limit || 500}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value) || 500)}
              className="game-history-filter-input"
            />
          </div>
        </div>
      </div>

      <div className="game-history-stats">
        Showing {filteredGames.length} unique games (from {games.length} total records)
      </div>

      <div className="game-history-list">
        {filteredGames.length === 0 ? (
          <div className="game-history-empty">No games found matching your criteria</div>
        ) : (
          <table className="game-history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>White Player</th>
                <th>Black Player</th>
                <th>Result</th>
                <th>Match Type</th>
                <th>Game ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredGames.map((group) => {
                // Determine which player record to use for replay (prefer whitePlayer, fallback to blackPlayer)
                const playerRecord = group.whitePlayer || group.blackPlayer;
                const canReplay = group.game_id && playerRecord;
                
                return (
                  <tr 
                    key={group.game_id}
                    onClick={() => {
                      if (canReplay) {
                        // Determine result from the player's perspective
                        let result: 'win' | 'loss' | 'draw' = 'draw';
                        if (group.winner === 'white' && group.whitePlayer) {
                          const playerResult = group.whitePlayer.result;
                          result = (playerResult === 'win' || playerResult === 'loss' || playerResult === 'draw') ? playerResult : 'draw';
                        } else if (group.winner === 'black' && group.blackPlayer) {
                          const playerResult = group.blackPlayer.result;
                          result = (playerResult === 'win' || playerResult === 'loss' || playerResult === 'draw') ? playerResult : 'draw';
                        } else if (group.winner === 'white' && !group.whitePlayer && group.blackPlayer) {
                          const playerResult = group.blackPlayer.result;
                          result = playerResult === 'win' ? 'loss' : playerResult === 'loss' ? 'win' : 'draw';
                        } else if (group.winner === 'black' && !group.blackPlayer && group.whitePlayer) {
                          const playerResult = group.whitePlayer.result;
                          result = playerResult === 'win' ? 'loss' : playerResult === 'loss' ? 'win' : 'draw';
                        }
                        
                        const playerColor: 'white' | 'black' = (playerRecord.player_color === 'white' || playerRecord.player_color === 'black') 
                          ? playerRecord.player_color 
                          : 'white';
                        
                        const winType: 'igo' | 'wego' | null = (playerRecord.win_type === 'igo' || playerRecord.win_type === 'wego') 
                          ? playerRecord.win_type 
                          : null;
                        
                        setSelectedReplay({
                          gameId: group.game_id,
                          opponent: playerRecord.opponent_username || playerRecord.opponent || 'Unknown',
                          result: result,
                          playerColor: playerColor,
                          timestamp: group.timestamp,
                          winType: winType,
                          gameEndReason: group.game_end_reason
                        });
                      }
                    }}
                    style={{ 
                      cursor: canReplay ? 'pointer' : 'default',
                      opacity: canReplay ? 1 : 0.7
                    }}
                    onMouseEnter={(e) => {
                      if (canReplay) {
                        e.currentTarget.style.backgroundColor = '#f0f4ff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (canReplay) {
                        e.currentTarget.style.backgroundColor = '';
                      }
                    }}
                  >
                    <td>{formatGameTimestamp(group.timestamp)}</td>
                    <td>
                      {group.whitePlayer 
                        ? (group.whitePlayer.user_username || `User ${group.whitePlayer.user_id}`)
                        : 'N/A'}
                    </td>
                    <td>
                      {group.blackPlayer 
                        ? (group.blackPlayer.user_username || `User ${group.blackPlayer.user_id}`)
                        : group.whitePlayer
                          ? (group.whitePlayer.opponent_username || group.whitePlayer.opponent || 'N/A')
                          : 'N/A'}
                    </td>
                    <td>
                      <span className={`game-result-badge ${group.winner === 'draw' ? 'draw' : group.winner === 'white' ? 'win' : 'loss'}`}>
                        {formatGameEndReason(group.game_end_reason, group.winner)}
                      </span>
                    </td>
                    <td>{group.game_mode}</td>
                    <td>
                      {group.game_id ? (
                        <code className="game-id-code">{group.game_id}</code>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Game Replay Modal */}
      {selectedReplay && (
        <GameReplay
          gameId={selectedReplay.gameId}
          onClose={() => setSelectedReplay(null)}
          opponent={selectedReplay.opponent}
          result={selectedReplay.result}
          playerColor={selectedReplay.playerColor}
          timestamp={selectedReplay.timestamp}
          winType={selectedReplay.winType}
          gameEndReason={selectedReplay.gameEndReason}
        />
      )}
    </div>
  );
};

